import { demoRuns } from '../domain/mock-data';
import { PARSER_VERSION } from '../domain/parser';
import type { ImportFilePayload, ImportResult, ImportSource } from '../domain/types';
import { getAnalysisWorker, hashText } from '../worker/client';
import { getCareerProgress, getDatasetOwnerId, getLatestImport, getSource, runRepository, saveDirectoryHandle } from './repositories';

async function filePayload(file: File, name = file.name): Promise<ImportFilePayload> {
  const text = await file.text();
  return { name, size: file.size, lastModified: file.lastModified, text, digest: await hashText(text) };
}

function manifestOf(payloads: ImportFilePayload[]): Record<string, string> {
  return Object.fromEntries(payloads.map((file) => [file.name, file.digest || `${file.size}:${file.lastModified}`]));
}

async function importPayloads(payloads: ImportFilePayload[], source: ImportSource, ownerId: string | null = null, manifest = manifestOf(payloads)): Promise<ImportResult> {
  if (!payloads.length) throw new Error('所选位置中没有可读取的 .run 或 progress.save 文件');
  const result = await (await getAnalysisWorker()).importFiles(payloads, source, {});
  if (result.failures.length) {
    const first = result.failures[0];
    throw new Error(`导入未生效：${result.failures.length} 个文件解析失败${first ? `（${first.file}：${first.error}）` : ''}`);
  }
  if (!result.runs.length && !result.progress) throw new Error('所选数据中没有可用的游戏记录或生涯存档');
  result.delta = { upsertRunIds: result.runs.map((run) => run.id), removedRunIds: [], progressChanged: Boolean(result.progress) };
  await runRepository.commitImport(source, result, manifest, ownerId);
  return result;
}

function filePath(file: File): string {
  return (file as File & { __relativePath?: string; webkitRelativePath?: string }).__relativePath || (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

function fileManifest(files: File[]): Record<string, string> {
  return Object.fromEntries(files.map((file) => [
    filePath(file),
    `${file.size}:${file.lastModified}`
  ]));
}

function coverageOf(runs: ImportResult['runs']): Record<string, number> {
  return {
    map: runs.filter((run) => run.capabilities.hasMap).length,
    choices: runs.filter((run) => run.capabilities.hasCardChoices).length,
    encounters: runs.filter((run) => run.capabilities.hasEncounterEvents).length,
    multiplayer: runs.filter((run) => run.isMultiplayer).length,
    version: runs.filter((run) => run.capabilities.hasVersion).length
  };
}

async function incrementalImport(files: File[], source: ImportSource, ownerId: string | null, manifest: Record<string, string>, previousManifest: Record<string, string>): Promise<ImportResult> {
  const [existingRuns, existingProgress] = await Promise.all([runRepository.list('all'), getCareerProgress()]);
  const filesByPath = new Map(files.map((file) => [filePath(file), file]));
  const changedPaths = [...filesByPath.keys()].filter((path) => previousManifest[path] !== manifest[path]);
  const removedPaths = Object.keys(previousManifest).filter((path) => !filesByPath.has(path));
  const changedFiles = changedPaths.map((path) => filesByPath.get(path)!).filter(Boolean);
  const progressChanged = changedPaths.some((path) => path.split('/').at(-1)?.toLowerCase() === 'progress.save') || removedPaths.some((path) => path.split('/').at(-1)?.toLowerCase() === 'progress.save');
  const changedRunPaths = new Set(changedPaths.filter((path) => path.toLowerCase().endsWith('.run')));
  const removedRunPaths = new Set(removedPaths.filter((path) => path.toLowerCase().endsWith('.run')));
  const existingByFile = new Map(existingRuns.map((run) => [run.fileName, run]));
  const removedRunIds = [...removedRunPaths].flatMap((path) => existingByFile.get(path)?.id ? [existingByFile.get(path)!.id] : []);

  let parsed: ImportResult = {
    runs: [], progress: null, failures: [], warnings: [], coverage: {}, changes: { added: 0, updated: 0, unchanged: 0, missing: [] }, parserVersion: PARSER_VERSION, importedAt: Date.now()
  };
  if (changedFiles.length) {
    const payloads = await Promise.all(changedFiles.map((file) => filePayload(file, filePath(file))));
    parsed = await (await getAnalysisWorker()).importFiles(payloads, source, {});
    if (parsed.failures.length) {
      const first = parsed.failures[0];
      throw new Error(`增量刷新未生效：${parsed.failures.length} 个变更文件解析失败${first ? `（${first.file}：${first.error}）` : ''}`);
    }
  }
  const added = [...changedRunPaths].filter((path) => !existingByFile.has(path)).length;
  const updated = [...changedRunPaths].filter((path) => existingByFile.has(path)).length;
  const unchanged = existingRuns.length - updated - removedRunIds.length;
  const progressRemoved = removedPaths.some((path) => path.split('/').at(-1)?.toLowerCase() === 'progress.save');
  const commitResult: ImportResult = {
    ...parsed,
    progress: progressChanged ? (progressRemoved ? null : parsed.progress) : existingProgress,
    changes: { added, updated, unchanged: Math.max(0, unchanged), missing: [...removedRunPaths] },
    delta: { upsertRunIds: parsed.runs.map((run) => run.id), removedRunIds, progressChanged }
  };
  await runRepository.commitIncremental(source, commitResult, manifest, removedRunIds, progressChanged, ownerId);
  const [runs, progress] = await Promise.all([runRepository.list('all'), getCareerProgress()]);
  return { ...commitResult, runs, progress, coverage: coverageOf(runs), warnings: [...commitResult.warnings, `增量刷新：${added} 个新增、${updated} 个更新、${removedRunIds.length} 个移除、${Math.max(0, unchanged)} 个复用。`] };
}

function sameManifest(left: Record<string, string>, right: Record<string, string>): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
}

async function unchangedResult(): Promise<ImportResult> {
  const [runs, progress] = await Promise.all([runRepository.list('all'), getCareerProgress()]);
  return {
    runs,
    progress,
    failures: [],
    warnings: ['目录文件没有变化，已跳过重复解析和写入。'],
    coverage: {
      map: runs.filter((run) => run.capabilities.hasMap).length,
      choices: runs.filter((run) => run.capabilities.hasCardChoices).length,
      encounters: runs.filter((run) => run.capabilities.hasEncounterEvents).length,
      multiplayer: runs.filter((run) => run.isMultiplayer).length,
      version: runs.filter((run) => run.capabilities.hasVersion).length
    },
    changes: { added: 0, updated: 0, unchanged: runs.length, missing: [] },
    parserVersion: runs[0]?.parserVersion || 'cached',
    importedAt: Date.now(),
    delta: { upsertRunIds: [], removedRunIds: [], progressChanged: false }
  };
}

export async function importFileList(files: FileList | File[], kind: 'folder-input' | 'single' = 'folder-input', ownerId: string | null = null): Promise<ImportResult> {
  const all = [...files].filter((file) => file.name.toLowerCase().endsWith('.run') || file.name.toLowerCase() === 'progress.save');
  const source: ImportSource = kind === 'single'
    ? { kind: 'single', id: 'primary-files', label: '选中的存档文件' }
    : { kind: 'folder-input', id: 'primary-folder', label: '本地存档文件夹' };
  if (kind === 'folder-input' && all.length) {
    const manifest = fileManifest(all);
    const [previous, datasetOwnerId, latestImport] = await Promise.all([getSource(source.id), getDatasetOwnerId(), getLatestImport()]);
    if (previous && datasetOwnerId === ownerId && latestImport?.result.parserVersion === PARSER_VERSION && sameManifest(previous.fileManifest, manifest)) return unchangedResult();
    if (previous && datasetOwnerId === ownerId && latestImport?.result.parserVersion === PARSER_VERSION) return incrementalImport(all, source, ownerId, manifest, previous.fileManifest);
    const payloads = await Promise.all(all.map((file) => filePayload(file, (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name)));
    return importPayloads(payloads, source, ownerId, manifest);
  }
  const payloads = await Promise.all(all.map((file) => filePayload(file, (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name)));
  return importPayloads(payloads, source, ownerId);
}

async function collectDirectoryFiles(root: FileSystemDirectoryHandle): Promise<File[]> {
  const files: File[] = [];
  async function walk(directory: FileSystemDirectoryHandle, prefix = '') {
    for await (const [name, entry] of directory.entries()) {
      const path = prefix ? `${prefix}/${name}` : name;
      if (entry.kind === 'directory') await walk(entry, path);
      else if (name.toLowerCase().endsWith('.run') || name.toLowerCase() === 'progress.save') {
        const file = await entry.getFile();
        Object.defineProperty(file, '__relativePath', { value: path });
        files.push(file);
      }
    }
  }
  await walk(root);
  return files;
}

async function childDirectory(directory: FileSystemDirectoryHandle, name: string): Promise<FileSystemDirectoryHandle | null> {
  try { return await directory.getDirectoryHandle(name); }
  catch { return null; }
}

async function profileSaveTarget(directory: FileSystemDirectoryHandle): Promise<{ saves: FileSystemDirectoryHandle; modifiedAt: number } | null> {
  try {
    const pointer = await directory.getFileHandle('profile.save');
    const file = await pointer.getFile();
    const value = JSON.parse(await file.text()) as { last_profile_id?: unknown };
    if (typeof value.last_profile_id !== 'string' || !value.last_profile_id) return null;
    const profile = await childDirectory(directory, value.last_profile_id);
    const saves = profile ? await childDirectory(profile, 'saves') : null;
    return saves ? { saves, modifiedAt: file.lastModified } : null;
  } catch { return null; }
}

export async function resolveSaveDirectory(root: FileSystemDirectoryHandle): Promise<FileSystemDirectoryHandle> {
  if (root.name.toLowerCase() === 'saves') return root;
  const directSaves = await childDirectory(root, 'saves');
  if (directSaves) return directSaves;
  const directProfile = await profileSaveTarget(root);
  if (directProfile) return directProfile.saves;
  const candidates: Array<{ saves: FileSystemDirectoryHandle; modifiedAt: number }> = [];
  for await (const [, entry] of root.entries()) {
    if (entry.kind !== 'directory') continue;
    const candidate = await profileSaveTarget(entry);
    if (candidate) candidates.push(candidate);
  }
  return candidates.sort((a, b) => b.modifiedAt - a.modifiedAt)[0]?.saves || root;
}

export async function importDirectory(handle: FileSystemDirectoryHandle, ownerId: string | null = null): Promise<ImportResult> {
  const permission = await handle.requestPermission({ mode: 'read' });
  if (permission !== 'granted') throw new Error('未获得存档目录读取权限');
  const saveDirectory = await resolveSaveDirectory(handle);
  const files = await collectDirectoryFiles(saveDirectory);
  if (!files.length) throw new Error('所选位置中没有可读取的 .run 或 progress.save 文件');
  const source: ImportSource = { kind: 'directory', id: 'primary-directory', label: saveDirectory.name || '本地存档目录' };
  const manifest = fileManifest(files);
  const [previous, datasetOwnerId, latestImport] = await Promise.all([getSource(source.id), getDatasetOwnerId(), getLatestImport()]);
  if (previous && datasetOwnerId === ownerId && latestImport?.result.parserVersion === PARSER_VERSION && sameManifest(previous.fileManifest, manifest)) {
    const result = await unchangedResult();
    await saveDirectoryHandle(saveDirectory).catch(() => undefined);
    return result;
  }
  if (previous && datasetOwnerId === ownerId && latestImport?.result.parserVersion === PARSER_VERSION) {
    const result = await incrementalImport(files, source, ownerId, manifest, previous.fileManifest);
    await saveDirectoryHandle(saveDirectory).catch(() => undefined);
    return result;
  }
  const payloads = await Promise.all(files.map((file) => filePayload(file, (file as File & { __relativePath?: string }).__relativePath || file.name)));
  const result = await importPayloads(payloads, source, ownerId, manifest);
  await saveDirectoryHandle(saveDirectory).catch(() => undefined);
  return result;
}

type WebkitFileEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (success: (file: File) => void, failure?: (error: DOMException) => void) => void;
  createReader?: () => { readEntries: (success: (entries: WebkitFileEntry[]) => void, failure?: (error: DOMException) => void) => void };
};

function relativeFile(file: File, path: string) {
  Object.defineProperty(file, '__relativePath', { value: path, configurable: true });
  return file;
}

async function filesFromWebkitEntry(entry: WebkitFileEntry, prefix = ''): Promise<File[]> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile && entry.file) {
    const file = await new Promise<File>((resolve, reject) => entry.file!(resolve, reject));
    return [relativeFile(file, path)];
  }
  if (!entry.isDirectory || !entry.createReader) return [];
  const reader = entry.createReader();
  const children: WebkitFileEntry[] = [];
  while (true) {
    const batch = await new Promise<WebkitFileEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    children.push(...batch);
  }
  return (await Promise.all(children.map((child) => filesFromWebkitEntry(child, path)))).flat();
}

export async function importDroppedItems(dataTransfer: DataTransfer, ownerId: string | null = null): Promise<ImportResult> {
  const items = [...dataTransfer.items];
  const handles = (await Promise.all(items.map(async (item) => {
    const method = (item as DataTransferItem & { getAsFileSystemHandle?: () => Promise<FileSystemHandle | null> }).getAsFileSystemHandle;
    return method ? method.call(item) : null;
  }))).filter((handle): handle is FileSystemHandle => Boolean(handle));
  if (handles.length === 1 && handles[0].kind === 'directory') return importDirectory(handles[0] as FileSystemDirectoryHandle, ownerId);
  if (handles.length) {
    const files: File[] = [];
    for (const handle of handles) {
      if (handle.kind === 'file') files.push(await (handle as FileSystemFileHandle).getFile());
      else files.push(...await collectDirectoryFiles(await resolveSaveDirectory(handle as FileSystemDirectoryHandle)));
    }
    return importFileList(files, 'folder-input', ownerId);
  }
  const entries = items.flatMap((item) => {
    const entry = (item as unknown as { webkitGetAsEntry?: () => unknown }).webkitGetAsEntry?.();
    return entry ? [entry as WebkitFileEntry] : [];
  });
  if (entries.length) return importFileList((await Promise.all(entries.map((entry) => filesFromWebkitEntry(entry)))).flat(), 'folder-input', ownerId);
  return importFileList([...dataTransfer.files], 'folder-input', ownerId);
}

export async function loadDemoData(ownerId: string | null = null): Promise<ImportResult> {
  const payloads: ImportFilePayload[] = await Promise.all(demoRuns.map(async (demo, index) => {
    const raw = {
      win: Boolean(demo.win),
      ascension: Number(demo.ascension || 0),
      game_mode: 'standard',
      build_id: index < demoRuns.length / 2 ? 'v0.111.0' : 'v0.112.0',
      run_time: Number(demo.runTime || 0),
      start_time: Number(demo.startTime || 0),
      was_abandoned: false,
      killed_by_encounter: demo.killedBy || null,
      players: [{
        id: 1,
        character: `CHARACTER.${String(demo.character || 'IRONCLAD').toUpperCase()}`,
        deck: Array.from({ length: Number(demo.deckSize || 20) }, (_, cardIndex) => ({ id: `CARD.DEMO_${cardIndex % 12}`, floor_added_to_deck: cardIndex })),
        relics: [{ id: `RELIC.DEMO_${index % 6}`, floor_added_to_deck: 1 }]
      }],
      map_point_history: [Array.from({ length: Math.min(Number(demo.floor || 0), 52) }, (_, floor) => ({
        map_point_type: floor % 15 === 14 ? 'boss' : floor % 7 === 5 ? 'elite' : floor % 5 === 4 ? 'rest_site' : 'monster',
        rooms: [{ room_type: 'monster', model_id: `ENCOUNTER.DEMO_${floor % 8}`, turns_taken: 2 + (floor % 6) }],
        player_stats: [{ player_id: 1, current_hp: Math.max(0, 80 - floor), max_hp: 80, current_gold: 90 + floor * 3, damage_taken: floor % 9 }]
      }))]
    };
    const text = JSON.stringify(raw);
    return { name: `demo-${String(index + 1).padStart(3, '0')}.run`, size: text.length, lastModified: Date.now(), text, digest: await hashText(text) };
  }));
  const source: ImportSource = { kind: 'demo', id: 'demo', label: '演示数据' };
  return importPayloads(payloads, source, ownerId);
}
